import React, { createContext, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import type { TFunction } from 'i18next';

import './OnboardingReviewBanner.sass';

export type OnboardingStep =
	| 'stakes'
	| 'risks'
	| 'opportunities'
	| 'stakeholders'
	| 'fishbone'
	| 'indicators';
const BASE_STEPS: OnboardingStep[] = ['stakes', 'risks', 'opportunities', 'stakeholders'];
const PRO_STEPS: OnboardingStep[] = [...BASE_STEPS, 'fishbone', 'indicators'];
const SESSION_KEY = 'gerositus.onboarding.active';
const SESSION_TYPE_KEY = 'gerositus.onboarding.projectType';

const isProType = (type: string | null) => type === 'pro' || type === 'strategy';

type CtxValue = { active: boolean; projectType: string | null; deactivate: () => void };
const OnboardingContext = createContext<CtxValue>({ active: false, projectType: null, deactivate: () => {} });

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const location = useLocation();
	const [active, setActive] = useState<boolean>(
		() => typeof window !== 'undefined' && sessionStorage.getItem(SESSION_KEY) === '1'
	);
	const [projectType, setProjectType] = useState<string | null>(
		() => (typeof window !== 'undefined' ? sessionStorage.getItem(SESSION_TYPE_KEY) : null)
	);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		if (params.has('onboarding')) {
			sessionStorage.setItem(SESSION_KEY, '1');
			setActive(true);
			const typeParam = params.get('type');
			if (typeParam) {
				sessionStorage.setItem(SESSION_TYPE_KEY, typeParam);
				setProjectType(typeParam);
			}
		}
	}, [location.search]);

	const deactivate = React.useCallback(() => {
		sessionStorage.removeItem(SESSION_KEY);
		sessionStorage.removeItem(SESSION_TYPE_KEY);
		setActive(false);
		setProjectType(null);
	}, []);

	return (
		<OnboardingContext.Provider value={{ active, projectType, deactivate }}>
			{children}
		</OnboardingContext.Provider>
	);
};

// Toggle a body class so global CSS (header height override, padding reset)
// can react. Mounted only by the consumer that actually renders the banner —
// otherwise on non-review paths (visu, scenarios…) the normal navigation
// would inherit the padding reset and stick to the edge of the viewport.
export const useOnboardingBodyClass = (active: boolean) => {
	useEffect(() => {
		if (active) document.body.classList.add('onboarding-active');
		else document.body.classList.remove('onboarding-active');
		return () => document.body.classList.remove('onboarding-active');
	}, [active]);
};

const stepUrl = (step: OnboardingStep, projectId: string, t: TFunction): string => {
	switch (step) {
		case 'stakes':
			return `/projects/${projectId}/${t('pages.stakes.url')}`;
		case 'risks':
			return `/projects/${projectId}/risks`;
		case 'opportunities':
			return `/projects/${projectId}/opportunities`;
		case 'stakeholders':
			return `/projects/${projectId}`;
		case 'fishbone':
			return `/projects/${projectId}/fishbone`;
		case 'indicators':
			return `/projects/${projectId}/${t('pages.indicators.url')}`;
	}
};

const deriveStepFromPath = (pathname: string, t: TFunction): OnboardingStep | null => {
	if (pathname.endsWith('/opportunities')) return 'opportunities';
	if (pathname.endsWith('/risks')) return 'risks';
	if (pathname.endsWith('/fishbone')) return 'fishbone';
	const stakesUrl = t('pages.stakes.url');
	if (pathname.endsWith(`/${stakesUrl}`)) return 'stakes';
	const indicatorsUrl = t('pages.indicators.url');
	if (pathname.endsWith(`/${indicatorsUrl}`)) return 'indicators';
	if (/^\/projects\/[^/]+\/?$/.test(pathname)) return 'stakeholders';
	return null;
};

const buildStepConfig = (step: OnboardingStep, t: TFunction) => {
	switch (step) {
		case 'stakes':
			return {
				title: t('pages.stakes.onboarding.title'),
				description: t('pages.stakes.onboarding.description'),
			};
		case 'risks':
			return {
				title: t('pages.risks.onboarding.risks.title'),
				description: t('pages.risks.onboarding.risks.description'),
			};
		case 'opportunities':
			return {
				title: t('pages.risks.onboarding.opportunities.title'),
				description: t('pages.risks.onboarding.opportunities.description'),
			};
		case 'stakeholders':
			return {
				title: t('pages.navigator.onboarding.title'),
				description: t('pages.navigator.onboarding.description'),
			};
		case 'fishbone':
			return {
				title: t('pages.fishbone.onboarding.title'),
				description: t('pages.fishbone.onboarding.description'),
			};
		case 'indicators':
			return {
				title: t('pages.indicators.onboarding.title'),
				description: t('pages.indicators.onboarding.description'),
			};
	}
};

export const useOnboardingActive = (): boolean => {
	return useContext(OnboardingContext).active;
};

export const useOnboardingStep = (page: OnboardingStep): { active: boolean } => {
	const ctx = useContext(OnboardingContext);
	const location = useLocation();
	const { t } = useTranslation();
	if (!ctx.active) return { active: false };
	return { active: deriveStepFromPath(location.pathname, t) === page };
};

// Returns the current onboarding step derived from the URL, or null when the
// current page is not part of the review flow (e.g. /summary, /scenarios).
// Callers use it to decide whether to render the review banner (step set) or
// fall back to the normal navigation (step null) — without losing the active
// onboarding session.
export const useOnboardingDerivedStep = (): OnboardingStep | null => {
	const location = useLocation();
	const { t } = useTranslation();
	return deriveStepFromPath(location.pathname, t);
};

export const OnboardingReviewBanner: React.FC = () => {
	const ctx = useContext(OnboardingContext);
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const params = useParams<{ id: string }>();
	const [closing, setClosing] = useState(false);

	if (!ctx.active) return null;
	const step = deriveStepFromPath(location.pathname, t);
	if (!step) return null;

	const steps = isProType(ctx.projectType) ? PRO_STEPS : BASE_STEPS;
	const idx = steps.indexOf(step);
	if (idx === -1) return null;
	const isLast = idx === steps.length - 1;

	const cfg = buildStepConfig(step, t);

	// Smooth exit : fade the banner out over 180ms then deactivate. We don't
	// collapse max-height (that produced a yo-yo: banner → 0 → nav 56px) — a
	// plain opacity fade leaves a single small snap when the nav appears.
	const closeWithAnimation = () => {
		setClosing(true);
		setTimeout(() => ctx.deactivate(), 180);
	};

	const handleNext = () => {
		if (isLast) {
			closeWithAnimation();
			return;
		}
		const nextStep = steps[idx + 1];
		history.push(stepUrl(nextStep, params.id, t));
	};

	const handleSkip = () => {
		closeWithAnimation();
	};

	return (
		<div
			className={`OnboardingReviewBanner${closing ? ' OnboardingReviewBanner--closing' : ''}`}
			data-testid={`onboarding-banner-${step}`}
		>
			<div className="OnboardingReviewBanner__main">
				<div className="OnboardingReviewBanner__head">
					<span className="OnboardingReviewBanner__step">
						{t('common.step', { defaultValue: 'Étape' })} {idx + 1}/{steps.length}
					</span>
					<span className="OnboardingReviewBanner__dot">·</span>
					<h2 className="OnboardingReviewBanner__title">{cfg.title}</h2>
				</div>
				<p className="OnboardingReviewBanner__description">{cfg.description}</p>
			</div>
			<div className="OnboardingReviewBanner__actions">
				<Button
					type="primary"
					size="small"
					onClick={handleNext}
					data-testid={`onboarding-next-${step}`}
				>
					{isLast ? t('actions.finish_review') : t('actions.next_step')}
				</Button>
				{!isLast && (
					<Button
						type="text"
						size="small"
						onClick={handleSkip}
						data-testid={`onboarding-skip-${step}`}
					>
						{t('actions.skip_review')}
					</Button>
				)}
			</div>
		</div>
	);
};

export default OnboardingReviewBanner;
